import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { viewStatementsApi } from './api'
import type { SplitPayload, UpdateStatementPayload } from './api'
import type { DeleteStatementsPayload, StatementsQueryArgs } from './types'

export function useAccounts() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'statements', 'accounts'),
    queryFn: viewStatementsApi.accounts,
  })
}

export function useStatements(args: StatementsQueryArgs) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(
      tenantId,
      'statements',
      'list',
      args.page,
      args.limit,
      args.sort.column,
      args.sort.direction,
      args.filters,
    ),
    queryFn: () => viewStatementsApi.list(args),
    placeholderData: (prev) => prev,
  })
}

export function useRequestDeleteCode() {
  return useMutation({
    mutationFn: () => viewStatementsApi.requestDeleteCode(),
  })
}

export function useDeleteStatements() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (payload: DeleteStatementsPayload) => viewStatementsApi.deleteData(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'statements') })
    },
  })
}

export function useUpdateStatement() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (args: { id: number; payload: UpdateStatementPayload }) =>
      viewStatementsApi.update(args.id, args.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'statements') })
    },
  })
}

export function useSplitStatement() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (args: { id: number; payload: SplitPayload }) =>
      viewStatementsApi.split(args.id, args.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'statements') })
    },
  })
}
